import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  FiTrendingUp, FiAlertCircle, FiDollarSign, FiMessageSquare,
  FiUsers, FiCheckSquare, FiFileText, FiUserPlus,
} from 'react-icons/fi';
import { usuarioLogado } from '../../services/auth';
import styles from './Dashboard.module.css';

const ICON_MAP = { FiUserPlus, FiMessageSquare, FiFileText, FiDollarSign };

const atividadesRecentes = [];

function getSaudacao() {
  const h = new Date().getHours();
  if (h < 12) return 'Bom dia';
  if (h < 18) return 'Boa tarde';
  return 'Boa noite';
}

function dataAtual() {
  const d = new Date().toLocaleDateString('pt-BR', {
    weekday: 'long', day: 'numeric', month: 'long', year: 'numeric',
  });
  return d.charAt(0).toUpperCase() + d.slice(1);
}

function moeda(v) {
  return v.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
}

function KpiCard({ icon: Icon, label, valor, corBg, corIcon, variacao, positiva }) {
  return (
    <div className={styles.kpiCard}>
      <div className={styles.kpiTop}>
        <span className={styles.kpiLabel}>{label}</span>
        <div className={styles.kpiIconWrapper} style={{ background: corBg }}>
          <Icon style={{ color: corIcon }} />
        </div>
      </div>
      <div className={styles.kpiValor}>{valor}</div>
      <div className={`${styles.kpiVariacao} ${positiva ? styles.variacaoPos : styles.variacaoNeg}`}>
        {positiva ? '↑' : '↓'} {variacao} este mês
      </div>
    </div>
  );
}

function Dashboard() {
  const navigate = useNavigate();
  const usuario = usuarioLogado();
  const primeiroNome = usuario?.nome?.split(' ')[0] ?? 'Usuário';
  const financeiro   = { receitaMes: 0, inadimplencia: 0, aReceber: 0, pagoHoje: 0 };
  const clientes     = { total: 0, novosMes: 0, contratoVencendo: 0, ativos: 0 };
  const atendimentos = { abertos: 0, pendentes: 0, resolvidosHoje: 0, atrasados: 0 };
  const rotinas      = { atrasadas: 0, vencemHoje: 0, concluidas: 0, total: 0 };
  const acessoRapido = [];

  return (
    <div className={styles.dashboard}>

      {/* Saudação */}
      <div className={styles.saudacao}>
        <h1 className={styles.saudacaoTitulo}>{getSaudacao()}, {primeiroNome}</h1>
        <p className={styles.saudacaoData}>{dataAtual()}</p>
      </div>

      {/* KPI Cards */}
      <div className={styles.kpiGrid}>
        <KpiCard
          icon={FiTrendingUp}
          label="Receita do mês"
          valor={moeda(financeiro.receitaMes)}
          corBg="#d1fae5" corIcon="#059669"
          variacao="+8%" positiva
        />
        <KpiCard
          icon={FiAlertCircle}
          label="Inadimplência"
          valor={moeda(financeiro.inadimplencia)}
          corBg="#fee2e2" corIcon="#dc2626"
          variacao="+2%" positiva={false}
        />
        <KpiCard
          icon={FiDollarSign}
          label="A receber"
          valor={moeda(financeiro.aReceber)}
          corBg="#dbeafe" corIcon="#2563eb"
          variacao="+5%" positiva
        />
        <KpiCard
          icon={FiMessageSquare}
          label="Atendimentos abertos"
          valor={atendimentos.abertos}
          corBg="#fef9c3" corIcon="#ca8a04"
          variacao="-3%" positiva={false}
        />
      </div>

      {/* Blocos de resumo */}
      <div className={styles.blocosGrid}>

        {/* Clientes */}
        <div className={styles.bloco}>
          <h2 className={styles.blocoTitulo}>
            <FiUsers className={styles.blocoTituloIcon} /> Clientes
          </h2>
          <div className={styles.blocoStatsRow}>
            <div className={styles.blocoStat}>
              <span className={styles.blocoStatLabel}>Total</span>
              <span className={styles.blocoStatValor}>{clientes.total}</span>
            </div>
            <div className={styles.blocoStat}>
              <span className={styles.blocoStatLabel}>Ativos</span>
              <span className={styles.blocoStatValor}>{clientes.ativos}</span>
            </div>
          </div>
          <div className={styles.blocoItem}>
            <span>Novos este mês</span>
            <strong>{clientes.novosMes}</strong>
          </div>
          <div className={styles.blocoItem}>
            <span>Contratos vencendo</span>
            <span className={`${styles.alerta} ${styles.alertaLaranja}`}>
              {clientes.contratoVencendo}
            </span>
          </div>
          <button className={styles.blocoBtn} onClick={() => navigate('/app/clientes')}>
            Ver clientes
          </button>
        </div>

        {/* Atendimentos */}
        <div className={styles.bloco}>
          <h2 className={styles.blocoTitulo}>
            <FiMessageSquare className={styles.blocoTituloIcon} /> Atendimentos
          </h2>
          <div className={styles.blocoStatsRow}>
            <div className={styles.blocoStat}>
              <span className={styles.blocoStatLabel}>Abertos</span>
              <span className={styles.blocoStatValor}>{atendimentos.abertos}</span>
            </div>
            <div className={styles.blocoStat}>
              <span className={styles.blocoStatLabel}>Pendentes</span>
              <span className={styles.blocoStatValor}>{atendimentos.pendentes}</span>
            </div>
          </div>
          <div className={styles.blocoItem}>
            <span>Resolvidos hoje</span>
            <strong>{atendimentos.resolvidosHoje}</strong>
          </div>
          <div className={styles.blocoItem}>
            <span>Atrasados</span>
            <span className={`${styles.alerta} ${styles.alertaVermelho}`}>
              {atendimentos.atrasados}
            </span>
          </div>
          <button className={styles.blocoBtn} onClick={() => navigate('/app/atendimento')}>
            Ver atendimentos
          </button>
        </div>

        {/* Rotinas */}
        <div className={styles.bloco}>
          <h2 className={styles.blocoTitulo}>
            <FiCheckSquare className={styles.blocoTituloIcon} /> Rotinas
          </h2>
          <div className={styles.blocoStatsRow}>
            <div className={styles.blocoStat}>
              <span className={styles.blocoStatLabel}>Total</span>
              <span className={styles.blocoStatValor}>{rotinas.total}</span>
            </div>
            <div className={styles.blocoStat}>
              <span className={styles.blocoStatLabel}>Concluídas</span>
              <span className={styles.blocoStatValor}>{rotinas.concluidas}</span>
            </div>
          </div>
          <div className={styles.blocoItem}>
            <span>Vencem hoje</span>
            <span className={`${styles.alerta} ${styles.alertaAmarelo}`}>
              {rotinas.vencemHoje}
            </span>
          </div>
          <div className={styles.blocoItem}>
            <span>Atrasadas</span>
            <span className={`${styles.alerta} ${styles.alertaVermelho}`}>
              {rotinas.atrasadas}
            </span>
          </div>
          <button className={styles.blocoBtn} onClick={() => navigate('/app/rotinas')}>
            Ver rotinas
          </button>
        </div>

      </div>

      {/* Acesso rápido + Atividade recente */}
      <div className={styles.duasColunasGrid}>

        {/* Acesso Rápido */}
        <div className={styles.bloco}>
          <h2 className={styles.blocoTitulo}>Acesso Rápido</h2>
          <div className={styles.acessoRapidoGrid}>
            {acessoRapido.map(({ label, icone, rota }) => {
              const Icon = ICON_MAP[icone];
              return (
                <button key={rota} className={styles.acessoBtn} onClick={() => navigate(rota)}>
                  {Icon && <Icon className={styles.acessoBtnIcon} />}
                  {label}
                </button>
              );
            })}
          </div>
        </div>

        {/* Atividade Recente */}
        <div className={styles.bloco}>
          <h2 className={styles.blocoTitulo}>Atividade Recente</h2>
          <ul className={styles.atividadeList}>
            {atividadesRecentes.map(({ icone: Icon, texto, tempo }) => (
              <li key={texto} className={styles.atividadeItem}>
                <div className={styles.atividadeIcone}><Icon /></div>
                <span className={styles.atividadeTexto}>{texto}</span>
                <span className={styles.atividadeTempo}>{tempo}</span>
              </li>
            ))}
          </ul>
        </div>

      </div>
    </div>
  );
}

export default Dashboard;
